import {
  useQuery,
} from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  AccountTreeRounded,
  GroupsRounded,
  RefreshRounded,
} from '@mui/icons-material';

import {
  getStacTeamReportingDay,
  getStacTeamReportingScope,
} from '../services/stacService';
import StacTeamTodayPanel
  from './StacTeamTodayPanel';


function getLocalDayKey(): string {
  const now = new Date();

  const year =
    now.getFullYear();

  const month = String(
    now.getMonth() + 1
  ).padStart(2, '0');

  const day = String(
    now.getDate()
  ).padStart(2, '0');

  return `${year}-${month}-${day}`;
}


function formatDuration(
  seconds: number,
): string {
  const totalMinutes =
    Math.floor(
      seconds / 60
    );

  const hours =
    Math.floor(
      totalMinutes / 60
    );

  const minutes =
    totalMinutes % 60;

  if (!hours) {
    return `${minutes} min`;
  }

  if (!minutes) {
    return `${hours} h`;
  }

  return `${hours} h ${minutes} min`;
}


export default function StacMyTeamView() {
  const scopeQuery = useQuery({
    queryKey: [
      'stac-team-reporting-scope',
    ],
    queryFn:
      getStacTeamReportingScope,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  });

  const todayKey =
    getLocalDayKey();

  const browserTimezone =
    Intl.DateTimeFormat()
      .resolvedOptions()
      .timeZone
    || 'UTC';

  const teamDayQuery = useQuery({
    queryKey: [
      'stac-team-reporting-day',
      todayKey,
      browserTimezone,
    ],
    queryFn: () =>
      getStacTeamReportingDay({
        day: todayKey,
        timezone:
          browserTimezone,
      }),
    enabled:
      scopeQuery.data?.has_scope
      === true,
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus:
      'always',
    refetchInterval:
      60_000,
  });

  if (scopeQuery.isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border:
            '1px solid rgba(148,163,184,0.16)',
          bgcolor:
            'rgba(8,18,36,0.88)',
          backdropFilter:
            'blur(10px)',
          px: {
            xs: 2.5,
            md: 3,
          },
          py: {
            xs: 4,
            md: 5,
          },
        }}
      >
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress
            sx={{
              color: '#38bdf8',
            }}
          />

          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              fontWeight: 600,
            }}
          >
            Cargando tu ámbito de equipo...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (
    scopeQuery.isError
    || !scopeQuery.data
  ) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border:
            '1px solid rgba(239,68,68,0.20)',
          bgcolor:
            'rgba(8,18,36,0.88)',
          backdropFilter:
            'blur(10px)',
          px: {
            xs: 2.5,
            md: 3,
          },
          py: {
            xs: 3,
            md: 3.5,
          },
        }}
      >
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={
                <RefreshRounded />
              }
              onClick={() => {
                void scopeQuery.refetch();
              }}
            >
              Reintentar
            </Button>
          }
        >
          No se pudo cargar tu ámbito
          de supervisión STAC.
        </Alert>
      </Paper>
    );
  }

  const scope =
    scopeQuery.data;

  const scopeRoots =
    scope.units
      .filter(
        (unit) =>
          unit.is_scope_root
      )
      .sort(
        (left, right) =>
          left.name.localeCompare(
            right.name,
            'es',
          )
      );

  const orderedUnits =
    [...scope.units].sort(
      (left, right) => {
        if (
          left.is_scope_root
          !== right.is_scope_root
        ) {
          return left.is_scope_root
            ? -1
            : 1;
        }

        return left.name.localeCompare(
          right.name,
          'es',
        );
      }
    );

  const dayWorkers =
    teamDayQuery.data?.workers
    ?? [];

  const activeWorkers =
    dayWorkers.filter(
      (worker) =>
        worker.active_entry
        !== null
    ).length;

  const totalRegisteredSeconds =
    dayWorkers.reduce(
      (total, worker) =>
        total
        + worker.total_seconds,
      0,
    );

  const refreshAll = () => {
    void scopeQuery.refetch();

    if (scope.has_scope) {
      void teamDayQuery.refetch();
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border:
            '1px solid rgba(148,163,184,0.18)',
          bgcolor:
            'rgba(15,23,42,0.66)',
          backdropFilter:
            'blur(10px)',
          px: {
            xs: 2.5,
            md: 3,
          },
          py: {
            xs: 2.5,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            lg: 'center',
          }}
          spacing={2}
        >
          <Stack
            spacing={1.25}
            sx={{
              maxWidth: 920,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <GroupsRounded
                sx={{
                  color: '#38bdf8',
                  fontSize: 26,
                }}
              />

              <Typography
                variant="h5"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Mi equipo
              </Typography>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: '#cbd5e1',
                lineHeight: 1.7,
              }}
            >
              Supervisa la actividad y la dedicación
              registrada por las personas incluidas
              en tu ámbito organizativo.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.65,
              }}
            >
              El contenido se limita a las ramas
              sobre las que tienes responsabilidad
              de supervisión en STAC.
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            startIcon={
              <RefreshRounded />
            }
            disabled={
              scopeQuery.isFetching
              || teamDayQuery.isFetching
            }
            onClick={refreshAll}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderColor:
                'rgba(56,189,248,0.34)',
              color: '#38bdf8',
              whiteSpace: 'nowrap',
            }}
          >
            Actualizar
          </Button>
        </Stack>
      </Paper>

      {!scope.has_scope ? (
        <Alert
          severity="info"
          variant="outlined"
        >
          Tienes permiso para consultar
          reporting de equipo, pero actualmente
          no eres responsable de ninguna unidad
          organizativa. Tu ámbito de supervisión
          está vacío.
        </Alert>
      ) : (
        <>
          <Grid
            container
            spacing={2.5}
          >
            {[
              {
                label:
                  'Personas visibles',
                value:
                  scope.workers.length,
                helper:
                  'Dentro de tu ámbito actual',
              },
              {
                label:
                  'En actividad',
                value:
                  teamDayQuery.isLoading
                    ? '—'
                    : activeWorkers,
                helper:
                  'Con una actividad abierta ahora',
              },
              {
                label:
                  'Unidades visibles',
                value:
                  scope.units.length,
                helper:
                  `${scopeRoots.length} ${
                    scopeRoots.length === 1
                      ? 'rama supervisada'
                      : 'ramas supervisadas'
                  }`,
              },
              {
                label:
                  'Registrado hoy',
                value:
                  teamDayQuery.isLoading
                    ? '—'
                    : formatDuration(
                        totalRegisteredSeconds
                      ),
                helper:
                  'Tiempo acumulado del ámbito',
              },
            ].map(
              (item) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  xl={3}
                  key={item.label}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      border:
                        '1px solid rgba(148,163,184,0.16)',
                      bgcolor:
                        'rgba(15,23,42,0.58)',
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#94a3b8',
                      }}
                    >
                      {item.label}
                    </Typography>

                    <Typography
                      variant="h5"
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                        mt: 0.4,
                      }}
                    >
                      {item.value}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: '#64748b',
                        mt: 0.5,
                        lineHeight: 1.45,
                      }}
                    >
                      {item.helper}
                    </Typography>
                  </Paper>
                </Grid>
              ),
            )}
          </Grid>

          <Grid
            container
            spacing={2.5}
            alignItems="stretch"
          >
            <Grid
              item
              xs={12}
              xl={9}
            >
              <StacTeamTodayPanel
                data={
                  teamDayQuery.data
                  ?? null
                }
                isLoading={
                  teamDayQuery.isLoading
                }
                isError={
                  teamDayQuery.isError
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              xl={3}
            >
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border:
                    '1px solid rgba(148,163,184,0.18)',
                  bgcolor:
                    'rgba(15,23,42,0.66)',
                  backdropFilter:
                    'blur(10px)',
                  px: 3,
                  py: 3,
                }}
              >
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <AccountTreeRounded
                      sx={{
                        color: '#38bdf8',
                      }}
                    />

                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 800,
                        }}
                      >
                        Ámbito supervisado
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#94a3b8',
                          mt: 0.5,
                          lineHeight: 1.6,
                        }}
                      >
                        Unidades incluidas en tus
                        ramas autorizadas.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box>
                    {orderedUnits.map(
                      (unit) => (
                        <Stack
                          key={unit.id}
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          spacing={1.25}
                          sx={{
                            py: 1.25,
                            borderBottom:
                              '1px solid rgba(148,163,184,0.08)',
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Box
                            sx={{
                              minWidth: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#e2e8f0',
                                fontWeight: 800,
                                fontSize: '0.875rem',
                              }}
                            >
                              {unit.name}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                color: '#64748b',
                                mt: 0.25,
                              }}
                            >
                              {
                                unit.direct_worker_count
                              }
                              {' '}
                              pers. ·
                              {' '}
                              {
                                unit.child_unit_count
                              }
                              {' '}
                              sub.
                            </Typography>
                          </Box>

                          {unit.is_scope_root ? (
                            <Chip
                              size="small"
                              label="Raíz"
                              variant="outlined"
                              sx={{
                                color: '#7dd3fc',
                                borderColor:
                                  'rgba(56,189,248,0.30)',
                                fontWeight: 800,
                              }}
                            />
                          ) : null}
                        </Stack>
                      ),
                    )}
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
}
